import { getContentHtmlStringValueFromRawRichTextContentState, IRichContentEditorState } from '@ViewModels';
import { css } from 'aphrodite';
import DOMPurify from 'dompurify';
import * as React from 'react';
import { createContentStateWithHtmlStringValue } from '../../../models/utils/richContentUtils';
import {
	useSupportMailboxQuery,
	useUpdateSupportMailboxMutation,
} from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../web/components/richContent/RichContentDocumentEditor';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { outgoingEmailTabStyles as styles } from './styles/outgoingEmailTabStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface IOutgoingEmailTabProps {
	mailboxId: string;
}

/** Extract HTML string from editor state */
const getEditorHtml = (state: IRichContentEditorState | null): string => {
	if (!state) return '';
	const raw = state.getRawRichTextContent();
	return getContentHtmlStringValueFromRawRichTextContentState(raw) || '';
};

const SignatureEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	minHeight: 120,
	skipDefaultEmailStyleFont: true,
	toolbar:
		'undo redo | fontselect fontsizeselect | bold italic underline | forecolor | link | listmenu | alignmentmenu',
};

export function OutgoingEmailTab({ mailboxId }: IOutgoingEmailTabProps) {
	const { data: mailbox, isLoading, isError } = useSupportMailboxQuery(mailboxId);
	const updateMutation = useUpdateSupportMailboxMutation(mailboxId);

	const [fromName, setFromName] = React.useState('');
	const [replyToAddress, setReplyToAddress] = React.useState('');
	const [isCsatEnabled, setIsCsatEnabled] = React.useState(true);
	const [signatureEditorState, setSignatureEditorState] = React.useState<IRichContentEditorState>(
		createContentStateWithHtmlStringValue('')
	);
	const [isDirty, setIsDirty] = React.useState(false);

	// Seed the form from server data ONCE per mailbox. A background refetch returns a
	// new `mailbox` reference; re-seeding then would clobber unsaved edits, so only
	// hydrate on first load or when switching to a different mailbox.
	const hydratedForIdRef = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (mailbox && hydratedForIdRef.current !== mailbox.id) {
			setFromName(mailbox.fromName || '');
			setReplyToAddress(mailbox.replyToAddress || mailbox.emailAddress || '');
			setIsCsatEnabled(mailbox.isCsatEnabled ?? true);
			setSignatureEditorState(createContentStateWithHtmlStringValue(mailbox.signature || ''));
			setIsDirty(false);
			hydratedForIdRef.current = mailbox.id;
		}
	}, [mailbox]);

	const handleSave = () => {
		if (!mailbox) return;
		const signature = getEditorHtml(signatureEditorState);
		// Partial PUT: send only the outbound fields this tab owns. Omitted fields
		// (isActive, isRoutingEnabled, name, etc.) are preserved server-side.
		updateMutation.mutate(
			{
				fromName: fromName.trim(),
				replyToAddress: replyToAddress.trim(),
				signature,
				isCsatEnabled,
			},
			{
				onSuccess: () => {
					setIsDirty(false);
				},
			}
		);
	};

	const getPreviewSignatureHtml = () => {
		const html = getEditorHtml(signatureEditorState);
		return DOMPurify.sanitize(html);
	};

	if (isLoading) return <div className={css(st.loading)}>Loading outgoing email settings...</div>;
	if (isError) return <div className={css(st.error)}>Failed to load outgoing email settings.</div>;

	return (
		<div className={css(styles.section)}>
			<h2 className={css(st.sectionTitle)}>Outgoing Email</h2>
			<div className={css(st.sectionDescription)}>How replies and forwards from this mailbox appear to customers.</div>

			<div className={css(st.card, styles.formCard)}>
				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>From Name</label>
					<Input
						type='text'
						value={fromName}
						onChange={e => {
							setFromName(e.target.value);
							setIsDirty(true);
						}}
						styles={[st.input]}
						placeholder='e.g. Levitate Support'
					/>
					<div className={css(st.hint)}>The name that appears in the &quot;From&quot; field of outgoing emails.</div>
				</div>

				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Reply-To Address</label>
					<Input
						type='text'
						value={replyToAddress}
						onChange={e => {
							setReplyToAddress(e.target.value);
							setIsDirty(true);
						}}
						styles={[st.input]}
						placeholder={mailbox?.emailAddress || 'support@example.com'}
					/>
					<div className={css(st.hint)}>Replies will be directed to this address. Defaults to the mailbox email.</div>
				</div>

				<div className={css(st.field)}>
					<label className={css(st.fieldLabel)}>Signature</label>
					<div className={css(st.editorContainer)}>
						<RichContentDocumentEditor
							contentState={signatureEditorState}
							onContentStateChanged={state => {
								setSignatureEditorState(state);
								setIsDirty(true);
							}}
							config={SignatureEditorConfig}
						/>
					</div>
				</div>

				<div className={css(st.field)}>
					<div className={css(st.toggleRow)}>
						<Button
							type='button'
							variant='ghost'
							styles={[st.toggle, isCsatEnabled && st.toggleActive]}
							onClick={() => {
								setIsCsatEnabled(!isCsatEnabled);
								setIsDirty(true);
							}}
							aria-pressed={isCsatEnabled}
							aria-label='Collect customer satisfaction ratings'
						>
							<span className={css(st.toggleKnob, isCsatEnabled && st.toggleKnobActive)} />
						</Button>
						<div className={css(st.toggleText)}>
							<span className={css(st.toggleTitle)}>Collect customer satisfaction ratings</span>
							<span className={css(st.toggleDescription)}>
								Adds a &quot;Rate your support&quot; link to replies sent from this mailbox.
							</span>
						</div>
					</div>
				</div>

				<div className={css(st.formActions)}>
					<Button onClick={handleSave} disabled={updateMutation.isPending || !isDirty}>
						{updateMutation.isPending ? 'Saving...' : 'Save Changes'}
					</Button>
					{updateMutation.isSuccess && !isDirty && <span className={css(st.successMsg)}>Settings saved.</span>}
					{updateMutation.isError && (
						<span className={css(st.errorMsg)}>
							{getApiErrorMessage(updateMutation.error, 'Failed to save settings.')}
						</span>
					)}
				</div>
			</div>

			<div className={css(st.card, styles.previewCard)}>
				<div className={css(styles.previewLabel)}>Preview</div>
				<div className={css(styles.previewFrame)}>
					<div className={css(styles.previewHeader)}>
						<div className={css(styles.previewFrom)}>
							<b>{fromName || 'Support'}</b>{' '}
							<span className={css(styles.previewMuted)}>&lt;{mailbox?.emailAddress || 'support@example.com'}&gt;</span>
						</div>
						<div className={css(styles.previewReplyTo)}>
							Reply-To: {replyToAddress || mailbox?.emailAddress || 'support@example.com'}
						</div>
					</div>
					<div className={css(styles.previewBody)}>
						<p className={css(styles.previewBodyPlaceholder)}>Your message content will appear here…</p>
						{getPreviewSignatureHtml() && (
							<>
								<div className={css(styles.previewSigDivider)} />
								<div dangerouslySetInnerHTML={{ __html: getPreviewSignatureHtml() }} />
							</>
						)}
						{isCsatEnabled && (
							<div className={css(styles.previewCsat)}>
								How was your support experience?{' '}
								<a
									href='#support-rating-preview'
									className={css(styles.previewCsatLink)}
									onClick={event => event.preventDefault()}
								>
									Rate your support
								</a>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
